<script setup lang="ts">
import { ref } from "vue";

/**
 * MessageInput is a simple, temporary component for user text input.
 * It will be replaced by the Tiptap-based CanvasEditor later.
 */

const props = defineProps<{
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "sendMessage", content: string): void;
}>();

const messageText = ref("");

const sendMessage = () => {
  if (props.disabled) return; // Don't send if disabled
  if (messageText.value.trim() !== "") {
    emit("sendMessage", messageText.value);
    messageText.value = ""; // Clear the input after sending
  }
};
</script>

<template>
  <div class="message-input-area">
    <form @submit.prevent="sendMessage" class="input-form">
      <input
        v-model="messageText"
        type="text"
        placeholder="Kirjoita viesti..."
        class="text-input"
        :class="{ disabled: props.disabled }"
        :disabled="props.disabled"
        autofocus
      />
      <button
        type="submit"
        class="send-button"
        :class="{ disabled: props.disabled }"
        :disabled="props.disabled"
      >
        Lähetä
      </button>
    </form>
  </div>
</template>

<style scoped>
.message-input-area {
  padding: 15px;
  border-top: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.input-form {
  display: flex;
  gap: 10px;
}

.text-input {
  flex-grow: 1;
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 10px 15px;
  font-size: 1rem;
}

.text-input:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
}

.send-button {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
}

.send-button:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.text-input.disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.send-button.disabled {
  background-color: #ccc;
  color: #999;
  cursor: not-allowed;
}

.send-button.disabled:hover {
  background-color: #ccc;
}
</style>
